import { useEffect, useRef } from 'react';
import './urban-network-loader.css';

/**
 * UrbanNetworkLoaderV2 — FASE 2 (loop continuo).
 *
 * Ciclo visual (loop infinito tras el fade-in inicial):
 *   FADE_IN (×1) → [ HOLD_URBAN → CONVERGE_FWD → HOLD_W → CONVERGE_REV ] → …
 *
 * La convergencia inversa es el espejo exacto de la directa:
 * los nodos regresan de FP[] a UP[], las aristas W se disuelven,
 * las calles reaparecen — sin corte ni parpadeo.
 *
 * Timing (md/xl): fade-in 700 ms + loop 1200+5000+800+5000 = 12 000 ms
 * Timing (sm/xs): fade-in 400 ms + loop  600+2000+600+2000 =  5 200 ms
 *
 * Animación vía requestAnimationFrame + setAttribute directo (sin re-renders React).
 * Archivo paralelo — no modifica UrbanNetworkLoader.tsx.
 */

export interface UrbanNetworkLoaderV2Props {
  size?: 'xs' | 'sm' | 'md' | 'xl';
  className?: string;
  /** @deprecated Solo se usaba en validación estática Fase 1. Sin efecto en Fase 2. */
  showW?: boolean;
}

// ── Timing por modo ───────────────────────────────────────────────
// FADE_IN ocurre una sola vez. El loop repite: HOLD_URBAN + CONVERGE + HOLD_W + CONVERGE.
const TIMING_FULL    = { FADE_IN: 700, HOLD_URBAN: 1200, CONVERGE: 5000, HOLD_W:  800 };
const TIMING_COMPACT = { FADE_IN: 400, HOLD_URBAN:  600, CONVERGE: 2000, HOLD_W:  600 };
const TIMING_XS      = { FADE_IN: 500, HOLD_URBAN:  900, CONVERGE: 3800, HOLD_W:  700 }; // topbar

// ── Posiciones finales (W alámbrica) — sin cambios respecto a v1 ──
const FP: [number, number][] = [
  [472.5,  853.5 ], [382.5,  695    ], [581,    686.5  ], [433.25, 608.5  ], [484,    522    ],
  [371,    672    ], [472.5,  506    ], [363,    485.5  ], [268.75, 485.5  ], [355,    299    ],
  [166.5,  299    ], [496.5,  506    ], [589.5,  672    ], [627,    602    ], [627,    299    ],
  [695.5,  402.5  ], [669.5,  672    ], [764,    506    ],
  [787.256,854    ], [678.847,686.849], [778.91, 613    ], [832.218,774.679],
  [877.181,695.357], [775.766,522.201], [787.256,506.187],
  [888.671,672.336], [939.753,579.002], [904.658,299    ],
  [990.836,485.668], [1093,   299    ],
];

// ── Posiciones iniciales — estructura urbana radial ──────────────
//
// Inspirada en ciudades europeas de crecimiento por anillos:
//   • Arteria principal horizontal (nodos 0–5)  — atraviesa la ciudad de borde a borde
//   • Anillo vial (nodos 6–13)                  — óvalo continuo alrededor del centro
//   • Distrito central (nodos 14–19)            — manzanas irregulares dentro del anillo
//   • Periferia exterior (nodos 20–29)          — barrios satélite conectados al anillo
//
// El conjunto es ilegible como W: la topología es radial, no simétrica.
// Con 8 s de animación los nodos migran a FP[] sin producir caos visual.
//
const UP: [number, number][] = [
  // ── arteria principal (izq → dcha, y≈552–568) ──────────────────
  [185,  568],  //  0  extremo izquierdo
  [340,  552],  //  1  artery-left  (cerca del anillo izq)
  [510,  545],  //  2  center-left
  [695,  552],  //  3  center-right
  [865,  545],  //  4  artery-right  (cerca del anillo dcha)
  [1072, 562],  //  5  extremo derecho

  // ── anillo vial (óvalo, ~240×185 px desde centro ≈ 600,560) ────
  [572,  372],  //  6  top
  [792,  398],  //  7  top-right
  [912,  548],  //  8  right
  [852,  698],  //  9  bottom-right
  [645,  778],  // 10  bottom
  [432,  752],  // 11  bottom-left
  [322,  615],  // 12  left
  [372,  428],  // 13  top-left

  // ── distrito central (hexágono irregular dentro del anillo) ─────
  [528,  462],  // 14  upper-left
  [668,  448],  // 15  upper-right
  [738,  615],  // 16  right
  [648,  672],  // 17  lower-right
  [525,  652],  // 18  lower-left
  [462,  548],  // 19  left

  // ── periferia exterior ─────────────────────────────────────────
  [248,  385],  // 20  outer upper-left
  [188,  490],  // 21  outer left
  [205,  668],  // 22  outer lower-left
  [288,  818],  // 23  outer bottom-left
  [462,  838],  // 24  outer bottom
  [932,  388],  // 25  outer upper-right
  [1065, 468],  // 26  outer right-upper
  [1088, 658],  // 27  outer right-lower
  [948,  778],  // 28  outer bottom-right
  [742,  832],  // 29  outer bottom
];

// ── Aristas urbanas (estado inicial) — 41 segmentos ──────────────
//
// Arteria principal (5) + anillo vial cerrado (8) +
// calles arteria↔anillo (3) + manzanas centrales (6) +
// diagonales al centro (6) + radiales anillo→periferia (8) +
// calles en barrios exteriores (5).
//
export const UE: [number, number][] = [
  // ── arteria principal ──────────────────────────────────────────
  [0,1], [1,2], [2,3], [3,4], [4,5],

  // ── anillo vial (óvalo cerrado) ────────────────────────────────
  [6,7], [7,8], [8,9], [9,10], [10,11], [11,12], [12,13], [13,6],

  // ── arteria ↔ anillo (empalmes) ────────────────────────────────
  [1,13],   // arteria entra al anillo por top-left
  [1,12],   // arteria entra al anillo por left
  [4,8],    // arteria sale del anillo por right

  // ── manzanas irregulares del centro ────────────────────────────
  [14,15], [15,16], [16,17], [17,18], [18,19], [19,14],

  // ── diagonales que conectan centro con arteria y anillo ────────
  [2,14],   // arteria → centro upper-left
  [3,15],   // arteria → centro upper-right
  [6,14],   // anillo top → centro upper-left
  [9,17],   // anillo bottom-right → centro lower-right
  [11,18],  // anillo bottom-left → centro lower-left
  [12,19],  // anillo left → centro left

  // ── radiales: anillo → periferia ───────────────────────────────
  [13,20],  // top-left → outer upper-left
  [12,21],  // left → outer left
  [11,23],  // bottom-left → outer bottom-left
  [10,24],  // bottom → outer bottom
  [7,25],   // top-right → outer upper-right
  [8,26],   // right → outer right-upper
  [9,28],   // bottom-right → outer bottom-right
  [10,29],  // bottom → outer bottom-right2

  // ── calles en barrios periféricos ──────────────────────────────
  [20,21],  // outer left chain upper
  [21,22],  // outer left chain lower
  [25,26],  // outer right chain upper
  [26,27],  // outer right chain mid
  [27,28],  // outer right chain lower
];

// ── Aristas de la W (topología final) ────────────────────────────
const EI: [number, number][] = [
  [0,1],[0,2],[0,3],[1,3],[1,2],[2,4],[4,3],[2,3],
  [5,6],[5,7],[5,8],[6,9],[6,7],[9,10],[9,7],[10,7],[10,8],[7,8],
  [11,12],[12,13],[11,14],[11,15],[11,13],[11,17],
  [13,16],[16,17],[13,17],[13,14],[17,15],[14,15],
  [18,19],[18,20],[18,21],[22,23],[22,21],[22,20],
  [23,19],[23,20],[19,20],[19,21],
  [24,25],[25,26],[24,27],[24,28],[24,26],[24,29],
  [27,29],[27,28],[29,28],[28,26],
];

// ── Radios de nodos ────────────────────────────────────────────────
const OUTER_R = [12,12,12,11,11,12,12,11,11,12,14,12,11,12,14,11,11,12,14,12,11,12,12,11,12,11,11,12,11,14];
const INNER_R = [6,5.5,5.5,5,5,5.5,5.5,5,5,5.5,7,5.5,5,5.5,7,5,5,5.5,7,5.5,5,5.5,5.5,5,5.5,5,5,5.5,5,7];

// ── Helpers ────────────────────────────────────────────────────────
function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function UrbanNetworkLoaderV2({
  size = 'md',
  className,
}: UrbanNetworkLoaderV2Props): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const cls = ['unl', `unl--${size}`, className].filter(Boolean).join(' ');
  const hideUE = size === 'xs';

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const T = size === 'xs' ? TIMING_XS : size === 'sm' ? TIMING_COMPACT : TIMING_FULL;
    // El loop repite HOLD_URBAN + CONVERGE_FWD + HOLD_W + CONVERGE_REV indefinidamente.
    const LOOP_DUR = T.HOLD_URBAN + T.CONVERGE + T.HOLD_W + T.CONVERGE;

    // Ventanas de fade dentro de cada CONVERGE (fracción 0–1 del segmento)
    const UE_FADE_END   = 0.55; // calles se disuelven en el primer 55 %
    const EI_FADE_START = 0.30; // aristas W aparecen desde el 30 %

    const ueLines    = svg.querySelectorAll<SVGLineElement>('.unl__ue');
    const elLines    = svg.querySelectorAll<SVGLineElement>('.unl__el');
    const elHiLines  = svg.querySelectorAll<SVGLineElement>('.unl__el-hi');
    const noCircles  = svg.querySelectorAll<SVGCircleElement>('.unl__no');
    const niCircles  = svg.querySelectorAll<SVGCircleElement>('.unl__ni');

    // Posiciones de calles urbanas: fijas en UP[], no cambian nunca
    for (let j = 0; j < UE.length; j++) {
      const [a, b] = UE[j];
      ueLines[j].setAttribute('x1', String(UP[a][0]));
      ueLines[j].setAttribute('y1', String(UP[a][1]));
      ueLines[j].setAttribute('x2', String(UP[b][0]));
      ueLines[j].setAttribute('y2', String(UP[b][1]));
    }

    let startTime: number | null = null;
    let rafId: number;

    function tick(now: number): void {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;

      let globalOpacity: number;
      let nodeT: number;    // 0 = UP[] (ciudad), 1 = FP[] (W)
      let ueOpacity: number;
      let eiOpacity: number;
      let hiOpacity = 0;   // capa de highlight W — solo activa en HOLD_W

      if (elapsed < T.FADE_IN) {
        // ── FADE_IN (una sola vez) ────────────────────────────────
        globalOpacity = elapsed / T.FADE_IN;
        nodeT    = 0;
        ueOpacity = hideUE ? 0 : 0.42;
        eiOpacity = 0;

      } else {
        // ── LOOP ─────────────────────────────────────────────────
        globalOpacity = 1;
        const loop = (elapsed - T.FADE_IN) % LOOP_DUR;

        const L0 = T.HOLD_URBAN;
        const L1 = L0 + T.CONVERGE;
        const L2 = L1 + T.HOLD_W;
        // L3 = L2 + T.CONVERGE = LOOP_DUR

        if (loop < L0) {
          // HOLD_URBAN — ciudad estática
          nodeT    = 0;
          ueOpacity = hideUE ? 0 : 0.42;
          eiOpacity = 0;

        } else if (loop < L1) {
          // CONVERGE_FWD — ciudad → W
          const ct = (loop - L0) / T.CONVERGE;
          nodeT = easeInOut(ct);
          ueOpacity = hideUE ? 0 : (
            ct < UE_FADE_END ? 0.42 * (1 - ct / UE_FADE_END) : 0
          );
          eiOpacity = ct < EI_FADE_START
            ? 0
            : Math.min(1, (ct - EI_FADE_START) / (1 - EI_FADE_START));

        } else if (loop < L2) {
          // HOLD_W — W estática + highlight al formarse
          nodeT    = 1;
          ueOpacity = 0;
          eiOpacity = 1;
          const ct = (loop - L1) / T.HOLD_W;
          if      (ct < 0.10) hiOpacity = easeOutCubic(ct / 0.10);
          else if (ct < 0.65) hiOpacity = 1;
          else if (ct < 0.95) hiOpacity = 1 - easeOutCubic((ct - 0.65) / 0.30);

        } else {
          // CONVERGE_REV — W → ciudad (espejo exacto de FWD)
          const ct = (loop - L2) / T.CONVERGE;   // 0 → 1
          nodeT = easeInOut(1 - ct);              // 1 → 0
          // EI se disuelve en el primer (1 - EI_FADE_START) = 70 %
          eiOpacity = ct < (1 - EI_FADE_START)
            ? 1 - ct / (1 - EI_FADE_START)
            : 0;
          // UE reaparece en el último UE_FADE_END = 55 %
          ueOpacity = hideUE ? 0 : (
            ct < (1 - UE_FADE_END)
              ? 0
              : 0.42 * (ct - (1 - UE_FADE_END)) / UE_FADE_END
          );
        }
      }

      // Aplicar opacidad global al SVG
      svg.style.opacity = String(globalOpacity);

      // Mover nodos
      for (let i = 0; i < 30; i++) {
        const cx = String(lerp(UP[i][0], FP[i][0], nodeT));
        const cy = String(lerp(UP[i][1], FP[i][1], nodeT));
        noCircles[i].setAttribute('cx', cx);
        noCircles[i].setAttribute('cy', cy);
        niCircles[i].setAttribute('cx', cx);
        niCircles[i].setAttribute('cy', cy);
      }

      // Calles urbanas: solo opacidad (posición ya fijada antes del loop)
      const ueStr = String(ueOpacity);
      for (let j = 0; j < ueLines.length; j++) {
        ueLines[j].setAttribute('stroke-opacity', ueStr);
      }

      // Aristas W: siguen a los nodos en movimiento
      const hiStr = String(hiOpacity);
      for (let j = 0; j < EI.length; j++) {
        const [a, b] = EI[j];
        const x1 = String(lerp(UP[a][0], FP[a][0], nodeT));
        const y1 = String(lerp(UP[a][1], FP[a][1], nodeT));
        const x2 = String(lerp(UP[b][0], FP[b][0], nodeT));
        const y2 = String(lerp(UP[b][1], FP[b][1], nodeT));
        elLines[j].setAttribute('x1', x1); elLines[j].setAttribute('y1', y1);
        elLines[j].setAttribute('x2', x2); elLines[j].setAttribute('y2', y2);
        elLines[j].setAttribute('stroke-opacity', String(eiOpacity));
        elHiLines[j].setAttribute('x1', x1); elHiLines[j].setAttribute('y1', y1);
        elHiLines[j].setAttribute('x2', x2); elHiLines[j].setAttribute('y2', y2);
        elHiLines[j].setAttribute('stroke-opacity', hiStr);
      }

      rafId = requestAnimationFrame(tick);
    }

    // Posición de inicio: nodos en UP[], calles visibles, W oculta
    for (let i = 0; i < 30; i++) {
      const cx = String(UP[i][0]);
      const cy = String(UP[i][1]);
      noCircles[i].setAttribute('cx', cx);
      noCircles[i].setAttribute('cy', cy);
      niCircles[i].setAttribute('cx', cx);
      niCircles[i].setAttribute('cy', cy);
    }
    for (let j = 0; j < ueLines.length; j++) {
      ueLines[j].setAttribute('stroke-opacity', hideUE ? '0' : '0.42');
    }
    for (let j = 0; j < elLines.length; j++) {
      elLines[j].setAttribute('stroke-opacity', '0');
    }
    for (let j = 0; j < elHiLines.length; j++) {
      elHiLines[j].setAttribute('stroke-opacity', '0');
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      svg.style.opacity = '';
    };
  }, [hideUE, size]);

  return (
    <svg
      ref={svgRef}
      viewBox="110 260 1040 625"
      className={cls}
      focusable="false"
      aria-hidden="true"
      overflow="visible"
    >
      <defs>
        {/* Glow suave para calles urbanas */}
        <filter id="unlv2-ue-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Glow más intenso para aristas W */}
        <filter id="unlv2-w-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.8" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Glow de nodos */}
        <filter id="unlv2-node-glow" x="-400%" y="-400%" width="900%" height="900%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b2" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="b2" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Glow intenso para el highlight de la W al completarse */}
        <filter id="unlv2-w-hi-glow" x="-160%" y="-160%" width="420%" height="420%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="5"  result="b2" />
          <feMerge>
            <feMergeNode in="b1" /><feMergeNode in="b2" /><feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Calles urbanas — más finas, menor glow */}
      <g
        stroke="#2ab8e8"
        strokeWidth="0.9"
        fill="none"
        strokeLinecap="round"
        filter="url(#unlv2-ue-glow)"
      >
        {UE.map((_, i) => (
          <line key={i} className="unl__ue" x1="0" y1="0" x2="0" y2="0" />
        ))}
      </g>

      {/* Aristas W — strokeWidth y glow originales */}
      <g
        stroke="#2ab8e8"
        strokeWidth="1.4"
        fill="none"
        strokeLinecap="round"
        filter="url(#unlv2-w-glow)"
      >
        {EI.map((_, i) => (
          <line key={i} className="unl__el" x1="0" y1="0" x2="0" y2="0" />
        ))}
      </g>

      {/* Aristas W — capa highlight (brillo al formarse) */}
      <g
        stroke="#c8f2ff"
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
        filter="url(#unlv2-w-hi-glow)"
      >
        {EI.map((_, i) => (
          <line key={i} className="unl__el-hi" x1="0" y1="0" x2="0" y2="0" />
        ))}
      </g>

      {/* Nodos outer glow */}
      <g fill="#18a0d0" opacity={0.55} filter="url(#unlv2-node-glow)">
        {OUTER_R.map((r, i) => (
          <circle key={i} className="unl__no" cx="627" cy="577" r={r} />
        ))}
      </g>

      {/* Nodos inner bright */}
      <g fill="#c8f2ff">
        {INNER_R.map((r, i) => (
          <circle key={i} className="unl__ni" cx="627" cy="577" r={r} />
        ))}
      </g>
    </svg>
  );
}
